import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import { SyntheticEvent, useEffect, useState } from "react";

import { usePipelineConfigSync } from "../../common/hooks/usePipelineConfigSync";
import { useFeatureFlagEnabled } from "../../common/posthog";
import { generateSystemPrompt } from "../../services/claude";
import { AnalysisGroups, PROTOCOLS } from "../../types/account";
import { EmailBccGroupsGrid } from "./components/AnalysisGroups";
import { EmailFlagsGrid } from "./components/EmailFlagsGrid";
import { UsersTab } from "./components/UsersTab";
import { useAccount } from "./hooks/useAccount";
import {
  getSavedSystemPrompt,
  resetSystemPrompt,
  saveSystemPrompt
} from "./utils/customSystemPromptStorage";

const AccountSettings = () => {
  const {
    account,
    emailFlagsDisplay,
    isLoading,
    error,
    isSaving,
    saveError,
    saveSuccess,
    saveAccount,
    updateEmailFlags
  } = useAccount();

  // Check if the custom system prompt feature is enabled
  const customSystemPromptEnabled = useFeatureFlagEnabled("custom-system-prompt");

  const accountAnalysisConfigEnabled = useFeatureFlagEnabled("account-analysis-configuration");
  const { syncPipelineConfig } = usePipelineConfigSync();

  // State for the custom system prompt
  const [customSystemPrompt, setCustomSystemPrompt] = useState<string>("");
  const [hasCustomPrompt, setHasCustomPrompt] = useState<boolean>(false);

  const [formValues, setFormValues] = useState({
    name: "",
    description: "",
    protocol_list: "",
    enabled: false,
    email_bcc_addresses: "",
    email_to_users: false,
    sample_rate_pct: 100,
    user_filter: "",
    analysis_mode: "all", // Can be "all", "percentage", or "specific"
    company_tone_and_language: ""
  });

  const [emailBccGroups, setEmailBccGroups] = useState<AnalysisGroups | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const usersTabIndex = accountAnalysisConfigEnabled ? 2 : 4;
  const systemPromptTabIndex = accountAnalysisConfigEnabled ? 3 : 5;

  useEffect(() => {
    if (account) {
      // Determine the analysis mode based on account values
      let analysisMode = "all";
      if (account.user_filter) {
        analysisMode = "specific";
      } else if (account.sample_rate_pct !== null && account.sample_rate_pct < 100) {
        analysisMode = "percentage";
      }

      setFormValues({
        name: account.name || "",
        description: account.description || "",
        protocol_list: account.protocol_list || "",
        enabled: account.enabled || false,
        email_bcc_addresses: account.email_bcc_addresses || "",
        email_to_users: account.email_to_users || false,
        sample_rate_pct: account.sample_rate_pct ?? 100,
        user_filter: account.user_filter || "",
        analysis_mode: analysisMode,
        company_tone_and_language: account.company_tone_and_language || ""
      });

      // Initialize email BCC groups
      setEmailBccGroups(account.email_bcc_groups || { groups: [] });
    }
  }, [account]);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Load custom system prompt from localStorage on mount
  useEffect(() => {
    if (customSystemPromptEnabled) {
      const savedPrompt = getSavedSystemPrompt();
      if (savedPrompt) {
        setCustomSystemPrompt(savedPrompt);
        setHasCustomPrompt(savedPrompt !== "");
        return;
      }
      setCustomSystemPrompt(generateSystemPrompt(formValues.company_tone_and_language || undefined));
    }
  }, [customSystemPromptEnabled, formValues.company_tone_and_language]);

  useEffect(() => {
    if (saveSuccess) {
      setShowSuccessMessage(true);
    }
  }, [saveSuccess]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: checked }));
  };

  const handleProtocolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues((prev) => ({ ...prev, protocol_list: e.target.value }));
  };

  const handleAnalysisModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const mode = e.target.value;
    setFormValues((prev) => ({
      ...prev,
      analysis_mode: mode,
      // Reset values when switching modes
      ...(mode === "all" && { sample_rate_pct: 100, user_filter: "" }),
      ...(mode === "percentage" && { user_filter: "" }),
      ...(mode === "specific" && { sample_rate_pct: 100 })
    }));
  };

  const handleEmailBccGroupsChange = (updatedGroups: AnalysisGroups) => {
    setEmailBccGroups(updatedGroups);
  };

  // Handle changes to the custom system prompt
  const handleSystemPromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomSystemPrompt(e.target.value);
  };

  // Save the custom system prompt to localStorage
  const handleSaveSystemPrompt = () => {
    saveSystemPrompt(customSystemPrompt);
    setHasCustomPrompt(customSystemPrompt !== "");
    setShowSuccessMessage(true);
  };

  // Reset the system prompt to default (empty in localStorage)
  const handleResetSystemPrompt = () => {
    resetSystemPrompt();
    setCustomSystemPrompt(generateSystemPrompt(formValues.company_tone_and_language || undefined));
    setHasCustomPrompt(false);
    setShowSuccessMessage(true);
  };

  // Validate email addresses in comma-separated list
  const validateEmails = (emails: string): boolean => {
    if (!emails) {
      return true;
    }

    const emailList = emails.split(",").map(email => email.trim());
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return emailList.every(email => email === "" || emailRegex.test(email));
  };

  const normaliseEmails = (emails: string): string => {
    if (!emails) {
      return "";
    }
    return emails.split(",").map(email => email.trim().toLowerCase()).join(",");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account) {
      return;
    }

    // Validate emails if in specific users mode
    if (formValues.analysis_mode === "specific" && !validateEmails(formValues.user_filter)) {
      alert("Please enter valid email addresses, separated by commas");
      return;
    }

    // Prepare user_filter and sample_rate_pct based on analysis mode
    let sample_rate_pct = account.sample_rate_pct;
    let user_filter = account.user_filter;

    if (formValues.analysis_mode === "all") {
      sample_rate_pct = 100;
      user_filter = null;
    } else if (formValues.analysis_mode === "percentage") {
      sample_rate_pct = Number(formValues.sample_rate_pct);
      user_filter = null;
    } else if (formValues.analysis_mode === "specific") {
      sample_rate_pct = 100;
      user_filter = formValues.user_filter;
    }

    const previousDescription = account.description;
    await saveAccount({
      ...account,
      name: formValues.name,
      description: formValues.description,
      protocol_list: formValues.protocol_list,
      enabled: formValues.enabled,
      email_bcc_addresses: normaliseEmails(formValues.email_bcc_addresses),
      email_to_users: formValues.email_to_users,
      sample_rate_pct,
      user_filter,
      company_tone_and_language: formValues.company_tone_and_language,
      email_bcc_groups: emailBccGroups
    }, emailFlagsDisplay || undefined);

    const descriptionChanged = (formValues.description || "") !== (previousDescription || "");
    if (descriptionChanged) {
      syncPipelineConfig();
    }
  };

  const handleSnackbarClose = () => {
    setShowSuccessMessage(false);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="calc(100vh - 200px)"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading account: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h1">
          Account Settings
        </Typography>
      </Box>

      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error saving changes: {saveError.message}
        </Alert>
      )}

      <form onSubmit={handleSubmit} autoComplete="off">
        <Paper sx={{ mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="account settings tabs"
              variant="fullWidth"
            >
              <Tab label="General Information" id="tab-0" aria-controls="tabpanel-0" />
              <Tab label="Email Settings" id="tab-1" aria-controls="tabpanel-1" />
              {!accountAnalysisConfigEnabled && (<Tab label="Analysis Groups" id="tab-2" aria-controls="tabpanel-2" />)}
              {!accountAnalysisConfigEnabled && (<Tab label="Analysis Configuration" id="tab-3" aria-controls="tabpanel-3" />)}
              <Tab label="Dashboard Users" id={`tab-${usersTabIndex}`} aria-controls={`tabpanel-${usersTabIndex}`} />
              {customSystemPromptEnabled && (
                <Tab label="System Prompt" id={`tab-${systemPromptTabIndex}`} aria-controls={`tabpanel-${systemPromptTabIndex}`} />
              )}
            </Tabs>
          </Box>

          {/* General Information Tab */}
          <Box
            role="tabpanel"
            hidden={tabValue !== 0}
            id="tabpanel-0"
            aria-labelledby="tab-0"
            sx={{ p: 3 }}
          >
            {tabValue === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Account Name"
                    name="name"
                    value={formValues.name}
                    onChange={handleChange}
                    required
                    disabled={isSaving}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Industry Lenses"
                    value={formValues.protocol_list || ""}
                    onChange={handleProtocolChange}
                    disabled={isSaving}
                    margin="normal"
                    SelectProps={{
                      displayEmpty: true
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {PROTOCOLS.map((protocol) => (
                      <MenuItem key={protocol} value={protocol}>
                        {protocol.replace(/-/g, " ")}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="My Company Overview"
                    name="description"
                    value={formValues.description}
                    onChange={handleChange}
                    multiline
                    rows={20}
                    disabled={isSaving}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Tone and Language"
                    name="company_tone_and_language"
                    value={formValues.company_tone_and_language}
                    onChange={handleChange}
                    multiline
                    rows={10}
                    disabled={isSaving}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formValues.enabled}
                          onChange={handleSwitchChange}
                          name="enabled"
                          color="primary"
                          disabled={isSaving}
                        />
                      }
                      label="Enabled"
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={isSaving}
                    >
                      {isSaving ? <CircularProgress size={24} /> : "Save Changes"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Email Settings Tab */}
          <Box
            role="tabpanel"
            hidden={tabValue !== 1}
            id="tabpanel-1"
            aria-labelledby="tab-1"
            sx={{ p: 3 }}
          >
            {tabValue === 1 && (
              <>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formValues.email_to_users}
                          onChange={handleSwitchChange}
                          name="email_to_users"
                          color="primary"
                          disabled={isSaving}
                        />
                      }
                      label="Send summary emails to users"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Central inbox for sending all summary emails to"
                      name="email_bcc_addresses"
                      value={formValues.email_bcc_addresses}
                      onChange={handleChange}
                      helperText="Comma-separated list of email addresses"
                      disabled={isSaving}
                      margin="normal"
                      autoComplete="off"
                      autoCapitalize="off"
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {emailFlagsDisplay && (
                  <EmailFlagsGrid
                    emailFlagsDisplay={emailFlagsDisplay}
                    onChange={updateEmailFlags}
                    disabled={isSaving}
                  />
                )}

                <Divider sx={{ my: 3 }} />

                <Box display="flex" justifyContent="flex-end" mt={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSaving}
                  >
                    {isSaving ? <CircularProgress size={24} /> : "Save Changes"}
                  </Button>
                </Box>
              </>
            )}
          </Box>

          {/* Groups Tab & Analysis Configuration Tab */}
          {!accountAnalysisConfigEnabled && (
            <>
              {/* Groups Tab */}
              <Box
                role="tabpanel"
                hidden={tabValue !== 2}
                id="tabpanel-2"
                aria-labelledby="tab-2"
                sx={{ p: 3 }}
              >
                {tabValue === 2 && (
                  <>
                    <EmailBccGroupsGrid
                      emailBccGroups={emailBccGroups}
                      onChange={handleEmailBccGroupsChange}
                      disabled={isSaving}
                    />

                    <Box display="flex" justifyContent="flex-end" mt={4}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isSaving}
                      >
                        {isSaving ? <CircularProgress size={24} /> : "Save Changes"}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>

              {/* Analysis Configuration Tab */}
              <Box
                role="tabpanel"
                hidden={tabValue !== 3}
                id="tabpanel-3"
                aria-labelledby="tab-3"
                sx={{ p: 3 }}
              >
                {tabValue === 3 && (
                  <>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          label="Which calls to analyse"
                          value={formValues.analysis_mode}
                          onChange={handleAnalysisModeChange}
                          disabled={isSaving}
                          margin="normal"
                        >
                          <MenuItem value="all">All calls</MenuItem>
                          <MenuItem value="percentage">Percentage of calls</MenuItem>
                          <MenuItem value="specific">Restrict to specific agents (by email address)</MenuItem>
                        </TextField>
                      </Grid>

                      {formValues.analysis_mode === "percentage" && (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Percentage of calls"
                            name="sample_rate_pct"
                            type="number"
                            value={formValues.sample_rate_pct}
                            onChange={handleChange}
                            inputProps={{ min: 0, max: 100 }}
                            disabled={isSaving}
                            margin="normal"
                            helperText="Enter a value between 0 and 100"
                          />
                        </Grid>
                      )}

                      {formValues.analysis_mode === "specific" && (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Email addresses of agents to analyse"
                            name="user_filter"
                            value={formValues.user_filter}
                            onChange={handleChange}
                            disabled={isSaving}
                            margin="normal"
                            multiline
                            rows={4}
                            helperText="Enter a comma-separated list of email addresses"
                          />
                        </Grid>
                      )}
                    </Grid>

                    <Box display="flex" justifyContent="flex-end" mt={4}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isSaving}
                      >
                        {isSaving ? <CircularProgress size={24} /> : "Save Changes"}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}

          {/* Users Tab */}
          <Box
            role="tabpanel"
            hidden={tabValue !== usersTabIndex}
            id={`tabpanel-${usersTabIndex}`}
            aria-labelledby={`tab-${usersTabIndex}`}
            sx={{ p: 3 }}
          >
            {tabValue === usersTabIndex && <UsersTab />}
          </Box>

          {/* System Prompt Tab - Only visible if feature flag is enabled */}
          {customSystemPromptEnabled && (
            <Box
              role="tabpanel"
              hidden={tabValue !== systemPromptTabIndex}
              id={`tabpanel-${systemPromptTabIndex}`}
              aria-labelledby={`tab-${systemPromptTabIndex}`}
              sx={{ p: 3 }}
            >
              {tabValue === systemPromptTabIndex && (
                <>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Custom System Prompt for Enlighten Me
                      </Typography>
                      <Typography variant="body2" gutterBottom color="text.secondary">
                        This custom system prompt will be used for all local Enlighten Me sessions. Leave empty to use the default system prompt.
                      </Typography>

                      {hasCustomPrompt && (
                        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                          You are currently using a custom system prompt for Enlighten Me.
                        </Alert>
                      )}

                      <TextField
                        fullWidth
                        label="Custom System Prompt"
                        value={customSystemPrompt}
                        onChange={handleSystemPromptChange}
                        multiline
                        rows={20}
                        margin="normal"
                      />
                    </Grid>
                  </Grid>

                  <Box display="flex" justifyContent="space-between" mt={4}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleResetSystemPrompt}
                    >
                      Reset to Default
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveSystemPrompt}
                    >
                      Save System Prompt
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Paper>
      </form>

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="success">
          Settings saved successfully
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountSettings;
